// @ts-nocheck
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Box,
  Grid,
  IconButton,
  Skeleton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import {
  FBoxContainer,
  FHAutocomplete,
  FHButton,
  FHSelect,
  FAntdTimePicker,
  FHConfirmDialog,
  FHWarning,
  AnimatedArrow,
} from '../../../base';
import { groupParentSupervisionActions, providerAssignmentActions } from '../../../../store';
import {
  FORTA_BLACK,
  FORTA_GRAY4,
  FORTA_BLUE,
  FORTA_ERROR,
  FORTA_GRAY3,
  FORTA_WHITE,
  FORTA_BROWN,
} from '../../../../utils/colors';
import {
  AttendanceType,
  GROUP_SN_MAX_FAMILIES,
  GROUP_SN_MIN_FAMILIES,
  SUPERVISION_LOCATION_V2,
} from '../constants';
import PlusIcon from '../../../svgs/PlusIcon';
import TrashIcon from '../../../svgs/TrashIcon';
import EditIcon from '../../../svgs/EditOutlinedIcon';
import ErrorAlertIcon from '../../../svgs/ErrorAlertIcon';
import {
  capitalizeFirstLetter,
  formatSecondsToHHMM3,
  getRoundedDiffInSecondsToQuarterHour,
} from '../../../../helpers';
import AttendanceTag from './AttendanceTag';
import { ClinicalRelationships } from '../../../../constants';

import type {
  IClientFamily,
  IFamilyAttendance,
  IOption,
  IProviderAssignment,
  ISelectOption,
  SelectValueType,
} from '../../../../utils/types';
import type { RootState } from '../../../../store';
import type { Moment } from 'moment';
import type { ChangeEvent, HTMLAttributes } from 'react';

interface IPartialAttendanceConvertContent {
  gpsnStartTime: Moment;
  gpsnEndTime: Moment;
  sessionDuration: number;
  attendance: IFamilyAttendance;
  dateStart: Moment;
  dateEnd: Moment;
  isOutsideRange: boolean;
  isDirty: boolean;
  setDateStart: (value: Moment) => void;
  setDateEnd: (value: Moment) => void;
  setIsDirty: (value: boolean) => void;
}

function PartialAttendanceConvertContent({
  gpsnStartTime,
  gpsnEndTime,
  sessionDuration,
  attendance,
  dateStart,
  dateEnd,
  isDirty,
  isOutsideRange,
  setDateStart,
  setDateEnd,
  setIsDirty,
}: IPartialAttendanceConvertContent) {
  const handleDateTimeUpdate = (field: string, value: Moment) => {
    if (!value || !value.isValid()) {
      return; // Don't update if value is null or invalid
    }

    let newDate, startTime, endTime;
    let newDateStart = moment(gpsnStartTime);
    let newDateEnd = moment(gpsnEndTime);
    const newValue = value;

    switch (field) {
      case 'date_start':
        newDate = moment(gpsnStartTime).startOf('day').format('YYYY-MM-DD');
        startTime = newValue.format('HH:mm');
        newDateStart = moment(`${newDate} ${startTime}`);
        setDateStart(newDateStart);
        break;
      case 'date_end':
        newDate = moment(gpsnStartTime).startOf('day').format('YYYY-MM-DD');
        endTime = newValue.format('HH:mm');
        newDateEnd = moment(`${newDate} ${endTime}`);
        setDateEnd(newDateEnd);
        break;
      default:
        break;
    }
  };

  // Disable rules for start and end pickers
  const buildDisabledHours = (minHour: number, maxHour: number) => {
    const disabled: number[] = [];

    // Ensure minHour and maxHour are valid
    const validMinHour = Math.max(0, Math.min(23, minHour));
    const validMaxHour = Math.max(0, Math.min(23, maxHour));

    for (let h = 0; h < 24; h++) {
      if (h < validMinHour || h > validMaxHour) {
        disabled.push(h);
      }
    }
    return disabled;
  };

  const buildDisabledMinutes = (selectedHour: number | null, min: Moment, max: Moment) => {
    const disabled: number[] = [];

    // If no hour is selected, don't disable any minutes
    if (selectedHour === null || selectedHour === undefined) {
      return disabled;
    }

    const minHour = min.hour();
    const maxHour = max.hour();
    const minMinute = min.minute();
    const maxMinute = max.minute();

    // Only disable minutes if we're at the boundary hours
    if (selectedHour === minHour && selectedHour === maxHour) {
      // Same hour - disable minutes outside the range
      for (let m = 0; m < minMinute; m++) disabled.push(m);
      for (let m = maxMinute + 1; m < 60; m++) disabled.push(m);
    } else if (selectedHour === minHour) {
      // At minimum hour - only disable minutes before minMinute
      for (let m = 0; m < minMinute; m++) disabled.push(m);
    } else if (selectedHour === maxHour) {
      // At maximum hour - only disable minutes after maxMinute
      for (let m = maxMinute + 1; m < 60; m++) disabled.push(m);
    }

    return disabled;
  };

  // Start time cannot be earlier than gpsnStartTime and cannot be after End time
  const startMin = moment(gpsnStartTime);
  const startMax = moment(dateEnd ? dateEnd : (attendance.date_end ?? gpsnEndTime)).subtract(
    900,
    'seconds',
  );
  const startDisabledHours = () => buildDisabledHours(startMin.hour(), startMax.hour());
  const startDisabledMinutes = (hour: number | null) =>
    buildDisabledMinutes(hour, startMin, startMax);

  // End time cannot be earlier than Start time and cannot be after gpsnEndTime
  const endMin = moment(dateStart ? dateStart : (attendance.date_start ?? gpsnStartTime)).add(
    900,
    'seconds',
  );
  const endMax = moment(gpsnEndTime);
  const endDisabledHours = () => buildDisabledHours(endMin.hour(), endMax.hour());
  const endDisabledMinutes = (hour: number | null) => buildDisabledMinutes(hour, endMin, endMax);

  // Ensure initial values are within valid ranges
  useEffect(() => {
    const currentStart = moment(dateStart);
    const currentEnd = moment(dateEnd);

    // Adjust start time if it's outside valid range
    if (currentStart.isBefore(startMin) || currentStart.isAfter(startMax)) {
      setDateStart(startMin.clone());
    }

    // Adjust end time if it's outside valid range
    if (currentEnd.isBefore(endMin) || currentEnd.isAfter(endMax)) {
      setDateEnd(endMax.clone());
    }
  }, [dateStart, dateEnd, startMin, startMax, endMin, endMax]);

  const startDisabledDateTime = () => ({
    disabledHours: () => startDisabledHours(),
    disabledMinutes: (hour: number | null) => startDisabledMinutes(hour),
  });

  const endDisabledDateTime = () => ({
    disabledHours: () => endDisabledHours(),
    disabledMinutes: (hour: number | null) => endDisabledMinutes(hour),
  });

  const newDuration = getRoundedDiffInSecondsToQuarterHour(dateStart, dateEnd);
  const currentState =
    sessionDuration === attendance.duration ? AttendanceType.Full : AttendanceType.Partial;
  const newState = sessionDuration === newDuration ? AttendanceType.Full : AttendanceType.Partial;

  return (
    <Box sx={{ my: 2 }}>
      {(!isDirty || currentState === newState) && (
        <Box mb={2}>
          {isOutsideRange ? (
            <AttendanceTag type={AttendanceType.Correction} />
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', mt: -1 }}>
              <Typography>Current attendance:</Typography>
              <AttendanceTag type={currentState} sx={{ ml: 0.5 }} />
            </Box>
          )}
        </Box>
      )}
      {isDirty && currentState !== newState && (
        <FHWarning
          sxText={{ ml: 1, color: FORTA_BLUE.primary, fontWeight: 700 }}
          sxContainer={{ backgroundColor: 'transparent', p: 0 }}
          content={`This will update ${attendance.client.first_name}'s family attendance from ${currentState} to ${newState}.`}
        />
      )}
      {isDirty && currentState !== newState && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', my: 2 }}>
          <AttendanceTag type={currentState} sx={{ mr: 2 }} />
          <AnimatedArrow color={FORTA_BLUE.primary} />
          <AttendanceTag type={newState} sx={{ ml: 2 }} />
        </Box>
      )}
      <Box mb={2}>
        <Typography fontWeight={700} mb={0.5}>
          Current
        </Typography>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 2,
            borderRadius: '8px',
            backgroundColor: FORTA_BLUE.light3,
            borderTop: `1px soild ${FORTA_BLUE.primary}`,
          }}
        >
          <Box>
            <Typography fontWeight={700}>Start</Typography>
            <Typography textAlign={'left'}>{moment(gpsnStartTime).format('h:mm a')}</Typography>
          </Box>
          <Box>
            <Typography fontWeight={700}>End</Typography>
            <Typography textAlign={'left'}>{moment(gpsnEndTime).format('h:mm a')}</Typography>
          </Box>
          <Box>
            <Typography fontWeight={700}>Duration</Typography>
            <Typography textAlign={'left'}>{formatSecondsToHHMM3(sessionDuration)}</Typography>
          </Box>
        </Box>
      </Box>
      <Box mb={2}>
        <Typography fontWeight={700} mb={0.5}>
          Updated
        </Typography>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 2,
            borderRadius: '8px',
            backgroundColor: FORTA_BLUE.light3,
            borderTop: `1px soild ${FORTA_BLUE.primary}`,
          }}
        >
          <Box>
            <Typography fontWeight={700}>Start</Typography>
            <FAntdTimePicker
              use12Hours
              allowClear={false}
              format="h:mm a"
              inputHeight={40}
              showNow={false}
              coloredBackground={false}
              style={{ width: '100%', boxShadow: 'none' }}
              value={moment(dateStart)}
              disabledTime={startDisabledDateTime}
              getPopupContainer={(trigger: any) => trigger?.parentElement || document.body}
              onSelect={(value: Moment) => {
                setIsDirty(true);
                handleDateTimeUpdate('date_start', value);
              }}
              onChange={(value: Moment | null) => {
                // Handle null values more gracefully
                if (!value) {
                  // If value is null, try to keep the current value
                  return;
                }
                setIsDirty(true);
                handleDateTimeUpdate('date_start', value);
              }}
            />
          </Box>
          <Box>
            <Typography fontWeight={700}>End</Typography>
            <FAntdTimePicker
              use12Hours
              allowClear={false}
              format="h:mm a"
              showNow={false}
              inputHeight={40}
              coloredBackground={false}
              style={{ width: '100%', boxShadow: 'none' }}
              value={moment(dateEnd)}
              disabledTime={endDisabledDateTime}
              getPopupContainer={(trigger: any) => trigger?.parentElement || document.body}
              onSelect={(value: Moment) => {
                setIsDirty(true);
                handleDateTimeUpdate('date_end', value);
              }}
              onChange={(value: Moment | null) => {
                // Handle null values more gracefully
                if (!value) {
                  // If value is null, try to keep the current value
                  return;
                }
                setIsDirty(true);
                handleDateTimeUpdate('date_end', value);
              }}
            />
          </Box>
          <Box>
            <Typography fontWeight={700}>Duration</Typography>
            <Typography>{formatSecondsToHHMM3(newDuration)}</Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

interface IFamilyRowItemProps {
  id: number;
  duration: number;
  location: string;
  isEditable: boolean;
  disabled: boolean;
  gpsnId?: number;
  gpsnDuration: number;
  gpsnStartTime: Moment;
  gpsnEndTime: Moment;
  attendance: IFamilyAttendance;
  providerAssignmentLoading: boolean;
  providerAssignments: IProviderAssignment[];
  isLastElement?: boolean;
  onDelete: (id: number) => void;
}

interface FamiliesProps {
  isEditable: boolean;
  validate: boolean;
}

function FamilyRowItem({
  id,
  location,
  isEditable,
  duration,
  disabled,
  gpsnId,
  attendance,
  gpsnDuration,
  gpsnStartTime,
  gpsnEndTime,
  providerAssignments,
  providerAssignmentLoading,
  onDelete,
}: IFamilyRowItemProps) {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const isSessionFullyAttended = gpsnDuration === duration;

  const client = `${attendance.client.first_name} ${attendance.client.last_name}`;
  const parent1 = attendance.parent_and_caregiver_one?.name
    ? `${attendance.parent_and_caregiver_one.name ?? ''}`
    : '';
  const parent2 = attendance.parent_and_caregiver_two?.name
    ? `${attendance.parent_and_caregiver_two.name ?? ''}`
    : '';

  const [openTimeEditModal, setOpenTimeEditModal] = useState<boolean>(false);
  const [dateStart, setDateStart] = useState<Moment>(moment(attendance.date_start));
  const [dateEnd, setDateEnd] = useState<Moment>(moment(attendance.date_end));
  const [isDirty, setIsDirty] = useState<boolean>(false);

  const pa = useMemo(() => {
    return providerAssignments.find((pa) => pa.client.id === attendance.client.id);
  }, [providerAssignments, attendance.client.id]);

  const isOutsideRange =
    moment(attendance.date_start).isBefore(moment(gpsnStartTime)) ||
    moment(attendance.date_end).isAfter(moment(gpsnEndTime));

  const handleEditFamilySessionTime = async () => {
    if (!gpsnId) return;
    const data = {
      response: attendance.response,
      date_start: dateStart.toISOString(),
      date_end: dateEnd.toISOString(),
    };
    const response = await dispatch(
      groupParentSupervisionActions.updateFamilyAttendance({
        getAccessTokenSilently,
        groupSNId: gpsnId,
        attendanceId: attendance.id,
        data,
      }),
    );
    if (!response.error) {
      setOpenTimeEditModal(false);
      setIsDirty(false);
    }
  };

  return (
    <Box color={FORTA_GRAY4}>
      <Grid
        container
        textAlign="left"
        sx={{
          alignItems: 'center',
          backgroundColor: FORTA_WHITE,
          border: `1px solid ${isOutsideRange ? FORTA_ERROR : FORTA_BLUE.light1}`,
          borderRadius: '8px',
        }}
        spacing={2}
        ml={-1}
        mt={2}
        pb={2}
        pt={0}
      >
        <Grid item xs={3.5}>
          <Typography color={FORTA_GRAY4}>
            <span style={{ fontWeight: 700 }}>Client: </span>
            {client}
          </Typography>
          <Typography color={FORTA_GRAY4}>
            <span style={{ fontWeight: 700 }}>Parent 1: </span> {parent1}
          </Typography>
          {parent2 && (
            <Typography color={FORTA_GRAY4}>
              <span style={{ fontWeight: 700 }}>Parent 2: </span> {parent2}
            </Typography>
          )}
        </Grid>
        <Grid item xs={2} sx={{ width: '100%' }}>
          <Typography color={FORTA_GRAY4}>{location}</Typography>
        </Grid>
        <Grid item xs={1.5} sx={{ width: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <AttendanceTag
              type={
                isOutsideRange
                  ? AttendanceType.Correction
                  : isSessionFullyAttended
                    ? AttendanceType.Full
                    : AttendanceType.Partial
              }
            />
            {!isSessionFullyAttended && (
              <Box mt={0.5}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
                  <Typography
                    ml={1}
                    fontWeight={700}
                    fontSize={12}
                    color={isOutsideRange ? FORTA_ERROR : FORTA_BROWN}
                  >{`${moment(attendance.date_start).format('h:mm a')} - ${moment(
                    attendance.date_end,
                  ).format('h:mm a')}`}</Typography>
                </Box>
              </Box>
            )}
          </Box>
        </Grid>
        <Grid item xs={2} sx={{ width: '100%' }}>
          {pa && (
            <Typography
              sx={{
                px: 1,
                ml: 1,
                borderRadius: '4px',
                width: '90px',
                color: FORTA_WHITE,
                fontWeight: 700,
                textAlign: 'center',
                backgroundColor: FORTA_BLUE.light1,
              }}
            >
              {capitalizeFirstLetter(pa?.clinical_relationship?.split('_')[0] ?? 'N/A')}
            </Typography>
          )}
          {providerAssignmentLoading && <Skeleton variant="text" width={90} height={24} />}
        </Grid>
        <Grid item xs={1.75} sx={{ width: '100%' }}>
          <Typography
            sx={{
              px: 1,
              borderRadius: '4px',
              width: '90px',
              color: FORTA_WHITE,
              fontWeight: 700,
              textAlign: 'center',
              backgroundColor: FORTA_BLUE.light1,
            }}
          >
            {attendance.bay ?? 'N/A'}
          </Typography>
        </Grid>
        {isEditable && (
          <Grid
            item
            xs={1.25}
            textAlign="center"
            width={'100%'}
            justifyContent="flex-end"
            display={'flex'}
          >
            <IconButton
              sx={{
                p: 0.5,
                borderRadius: '8px',
                color: FORTA_BLUE.primary,
              }}
              disabled={disabled}
              onClick={() => {
                if (disabled) return;
                setOpenTimeEditModal(true);
              }}
            >
              <EditIcon color={FORTA_BLUE.primary} width={16} height={16} />
            </IconButton>
            <IconButton
              sx={{
                p: 0.5,
                borderRadius: '8px',
                color: FORTA_BLUE.primary,
                mx: 1,
              }}
              onClick={() => onDelete(id)}
              disabled={disabled}
              data-testid={`trash_${client}_${parent1}_${location}`.split(' ').join('_')}
            >
              <TrashIcon color={FORTA_BLUE.primary} width={18} height={18} />
            </IconButton>
          </Grid>
        )}
      </Grid>
      {isEditable && (
        <FHConfirmDialog
          open={openTimeEditModal}
          title={'Update family attendance time'}
          dialogHeight={'auto'}
          contentRenderer={() => (
            <PartialAttendanceConvertContent
              sessionDuration={gpsnDuration}
              gpsnStartTime={gpsnStartTime}
              gpsnEndTime={gpsnEndTime}
              attendance={attendance}
              dateStart={dateStart}
              dateEnd={dateEnd}
              isDirty={isDirty}
              isOutsideRange={isOutsideRange}
              setDateStart={setDateStart}
              setDateEnd={setDateEnd}
              setIsDirty={setIsDirty}
            />
          )}
          sx={{
            '& .MuiModal-backdrop': {
              backdropFilter: 'blur(2px)',
              backgroundColor: 'rgba(0, 0, 0, 0.3)',
            },
          }}
          submittext={'Submit'}
          canceltext={'Cancel'}
          disableSubmitButton={!isDirty && !isOutsideRange}
          onClose={() => {
            setOpenTimeEditModal(false);
            setIsDirty(false);
            setDateStart(moment(attendance.date_start));
            setDateEnd(moment(attendance.date_end));
          }}
          onSubmit={() => {
            handleEditFamilySessionTime();
          }}
        />
      )}
    </Box>
  );
}

function FamilyListHeader() {
  return (
    <Grid container textAlign="left" mt={3} spacing={2}>
      <Grid item xs={3.5}>
        <Typography fontWeight="bold">Family details</Typography>
      </Grid>
      <Grid item xs={2} sx={{ width: '100%' }}>
        <Typography fontWeight="bold" sx={{ width: '100%' }}>
          Client location
        </Typography>
      </Grid>
      <Grid item xs={1.5} sx={{ width: '100%', pl: 2 }}>
        <Typography fontWeight="bold" sx={{ width: '100%' }}>
          Attendance
        </Typography>
      </Grid>
      <Grid item xs={2} sx={{ width: '100%', pl: 2 }}>
        <Typography fontWeight="bold" sx={{ width: '100%' }}>
          Provider assignment
        </Typography>
      </Grid>
      <Grid item xs={1.75} sx={{ width: '100%', pl: 2 }}>
        <Typography fontWeight="bold" sx={{ width: '100%' }}>
          Client bay
        </Typography>
      </Grid>
    </Grid>
  );
}

function Families({ isEditable, validate }: FamiliesProps) {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { current: groupParentSN, loading: groupSNLoading } = useSelector(
    (state: RootState) => state.groupParentSupervision,
  );
  const { list: providerAssignments = [], loading: providerAssignmentLoading } = useSelector(
    (state: RootState) => state.providerAssignment,
  );
  const { clientsFamily = [] } = useSelector((state: RootState) => state.clients);

  const [clientOptions, setClientOptions] = useState<Array<IOption>>([]);
  const [parentOptions, setParentOptions] = useState<Array<ISelectOption>>([]);
  const [selectedClient, setSelectedClient] = useState<IOption>();
  const [selectedParentOne, setSelectedParentOne] = useState<SelectValueType>();
  const [selectedParentTwo, setSelectedParentTwo] = useState<SelectValueType>();
  const [location, setLocation] = useState<SelectValueType>();
  const [families, setFamilies] = useState<Array<IFamilyAttendance>>([]);

  const disableAddFamily = !selectedClient || !selectedParentOne || !location;

  useEffect(() => {
    setSelectedParentOne(undefined);
    setSelectedParentTwo(undefined);
    if (!selectedClient || !clientsFamily) return;
    const clientFamilyData = clientsFamily.find((item) => item.client.id === selectedClient.id);
    if (!clientFamilyData) return;

    const parentOptions =
      clientFamilyData.parents_and_caregivers?.map((item) => ({
        value: item.id,
        label: item.name,
      })) ?? [];
    setParentOptions(parentOptions);
  }, [selectedClient]);

  useEffect(() => {
    if (!groupParentSN?.bcba?.id) return;
    dispatch(
      providerAssignmentActions.getList({
        getAccessTokenSilently,
        active_only: true,
        provider_id: groupParentSN.bcba.id,
        clinical_relationship: [
          ClinicalRelationships.HomeBcba,
          ClinicalRelationships.BobbingBcba,
          ClinicalRelationships.SubstituteBCBA,
        ],
      }),
    );
  }, [groupParentSN?.id]);

  useEffect(() => {
    if (!groupParentSN) {
      setClientOptions([]);
      setParentOptions([]);
      setSelectedClient(undefined);
      setSelectedParentOne(undefined);
      setSelectedParentTwo(undefined);
      setLocation(undefined);
      setFamilies([]);
      return;
    }
    setFamilies(groupParentSN.family_attendance ?? []);
    const selectedClientIds =
      groupParentSN.family_attendance?.map((it: IFamilyAttendance) => Number(it.client.id)) ?? [];

    const options: IOption[] =
      clientsFamily?.map((item: IClientFamily) => ({
        id: item.client.id,
        label: `${item.client.first_name} ${item.client.last_name}`,
        description:
          providerAssignments
            .find(
              (pa) =>
                pa.client.id === item.client.id &&
                pa.clinical_relationship !== ClinicalRelationships.HomeBcba, // Don't show label for home BCBA
            )
            ?.clinical_relationship?.split('_')[0] ?? '',
      })) ?? [];
    const filteredClientOptions =
      options?.filter((it) => it.id && !selectedClientIds.includes(Number(it.id))) ?? [];
    setClientOptions(filteredClientOptions);
  }, [groupParentSN, clientsFamily, providerAssignments]);

  const handleFamilyAttendanceDelete = (id: number) => {
    if (!groupParentSN) return;
    dispatch(
      groupParentSupervisionActions.deleteFamilyAttendance({
        getAccessTokenSilently,
        groupSNId: groupParentSN?.id,
        attendanceId: id,
      }),
    );
  };

  const handleAddNewFamily = async () => {
    if (!selectedClient || !selectedParentOne || !location || !groupParentSN) return;
    const data = {
      client_id: selectedClient.id,
      parent_and_caregiver_one_id: selectedParentOne,
      parent_and_caregiver_two_id: selectedParentTwo,
      location: location,
      date_start: groupParentSN.date_start,
      date_end: moment(groupParentSN.date_start).add(groupParentSN.duration, 'seconds'),
    };
    const response = await dispatch(
      groupParentSupervisionActions.addFamilyAttendance({
        getAccessTokenSilently,
        groupSNId: groupParentSN.id,
        data,
      }),
    );
    if (!response.payload) {
      return;
    }
    if (groupParentSN.training_topics.length > 0) {
      const bulkInsertData = {
        training_topic_ids: groupParentSN.training_topics?.map((t) => t.id),
        family_attendance_ids: [response.payload.id],
      };
      dispatch(
        groupParentSupervisionActions.createBulkTopicFamilyMappings({
          data: bulkInsertData,
          getAccessTokenSilently,
          groupSNId: groupParentSN.id,
        }),
      );
    }

    setSelectedClient(undefined);
    setSelectedParentOne(undefined);
    setSelectedParentTwo(undefined);
    setLocation(undefined);
  };

  const parentOneOptions = parentOptions.filter((item) => {
    if (selectedParentTwo && item.value === selectedParentTwo) {
      return false;
    }
    return true;
  });

  const parentTwoOptions = parentOptions.filter((item) => {
    if (selectedParentOne && item.value === selectedParentOne) {
      return false;
    }
    return true;
  });

  return (
    <FBoxContainer sx={{ flexGrow: 0, mt: 3, boxShadow: 'none' }}>
      <Typography fontSize={21} color={FORTA_BLUE.primary} textAlign={'left'}>
        Families attending
      </Typography>
      {(validate || families?.length > 0) &&
        families?.length < GROUP_SN_MIN_FAMILIES &&
        isEditable && (
          <Box sx={{ display: 'flex', mt: 3 }}>
            <ErrorAlertIcon width={18} height={18} viewBox="0 0 26 24" color={FORTA_ERROR} />
            <Typography
              textAlign={'left'}
              alignSelf="start"
              lineHeight={'20px'}
              ml={0.5}
              mt={-0.1}
              sx={{ cursor: 'pointer' }}
              color={FORTA_ERROR}
            >
              At least two families must be added
            </Typography>
          </Box>
        )}
      {isEditable && families?.length < GROUP_SN_MAX_FAMILIES && (
        <Box mt={4}>
          <Grid container spacing={3}>
            <Grid item md={3} sm={6} xs={12}>
              <Typography fontWeight="bold" textAlign="left" sx={{ mb: 1 }} noWrap>
                Client name*
              </Typography>
              <FHAutocomplete
                sx={{
                  width: '100%',
                  '& input::placeholder': {
                    color: `${FORTA_GRAY3} !important`,
                  },
                  boxShadow: 'none',
                }}
                inputHeight={40}
                data-testid="gpsnClientDropdown"
                disableClearable
                error={validate && families?.length < GROUP_SN_MIN_FAMILIES && !selectedClient}
                disabled={!groupParentSN?.id || groupSNLoading}
                value={selectedClient ?? ''}
                placeholder="Select client"
                isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
                renderOption={(props: HTMLAttributes<HTMLLIElement>, option: IOption) => {
                  return (
                    <Box
                      {...props}
                      sx={{ display: 'flex', justifyContent: 'space-between' }}
                      key={option.id}
                      data-testid={`clientLabel${option.id}`}
                    >
                      <Typography>{option.label}</Typography>
                      {option.description && (
                        <Typography
                          sx={{
                            px: 1,
                            ml: 1,
                            borderRadius: '4px',
                            color: FORTA_WHITE,
                            fontWeight: 700,
                            backgroundColor: FORTA_BLUE.light1,
                          }}
                        >
                          {capitalizeFirstLetter(option.description)}
                        </Typography>
                      )}
                    </Box>
                  );
                }}
                options={clientOptions}
                onChange={(_: ChangeEvent<HTMLSelectElement>, newValue: IOption | null) => {
                  if (newValue) {
                    setSelectedClient(newValue);
                  }
                }}
              />
            </Grid>
            <Grid item md={2.5} sm={6} xs={12}>
              <Typography fontWeight="bold" textAlign="left" sx={{ mb: 1 }} noWrap>
                Parent 1*
              </Typography>
              <Box sx={{ display: 'flex', position: 'relative', flexGrow: 1 }}>
                <FHSelect
                  data-testid="groupSNParentOneField"
                  sx={{
                    flexGrow: 1,
                    maxWidth: '100%',
                    '&.Mui-disabled': {
                      WebkitTextFillColor: FORTA_GRAY3,
                      cursor: 'not-allowed',
                    },
                    boxShadow: 'none',
                  }}
                  inputHeight={40}
                  displayEmpty
                  renderValue={
                    !selectedParentOne
                      ? () => <Typography color={FORTA_GRAY3}>Select parent</Typography>
                      : undefined
                  }
                  disabled={!isEditable || !selectedClient || groupSNLoading}
                  error={
                    validate &&
                    selectedClient &&
                    families?.length < GROUP_SN_MIN_FAMILIES &&
                    !selectedParentOne
                  }
                  options={parentOneOptions}
                  optionDataTestIdPrefix="groupSNParentOneField"
                  value={selectedParentOne}
                  onUpdate={(value: SelectValueType) => {
                    setSelectedParentOne(value);
                  }}
                />
              </Box>
            </Grid>
            <Grid item md={2.5} sm={6} xs={12}>
              <Typography fontWeight="bold" textAlign="left" sx={{ mb: 1 }} noWrap>
                Parent 2
              </Typography>
              <Box sx={{ display: 'flex', position: 'relative', flexGrow: 1 }}>
                <FHSelect
                  data-testid="groupSNParentTwoField"
                  sx={{
                    flexGrow: 1,
                    maxWidth: '100%',
                    '&.Mui-disabled': {
                      WebkitTextFillColor: FORTA_GRAY3,
                      cursor: 'not-allowed',
                    },
                    boxShadow: 'none',
                  }}
                  inputHeight={40}
                  disabled={!isEditable || !selectedClient || groupSNLoading}
                  displayEmpty
                  renderValue={
                    !selectedParentTwo
                      ? () => (
                          <Typography color={`${FORTA_GRAY3} !important`}>Select parent</Typography>
                        )
                      : undefined
                  }
                  options={parentTwoOptions}
                  optionDataTestIdPrefix="groupSNParentTwoField"
                  value={selectedParentTwo}
                  selectFirstOption={false}
                  clearable
                  onUpdate={(value: SelectValueType) => {
                    setSelectedParentTwo(value);
                  }}
                  onClear={() => {
                    setSelectedParentTwo(undefined);
                  }}
                />
              </Box>
            </Grid>
            <Grid item md={2.5} sm={6} xs={12}>
              <Typography fontWeight="bold" textAlign="left" sx={{ mb: 1 }} noWrap>
                Client location*
              </Typography>
              <Box sx={{ display: 'flex', position: 'relative', flexGrow: 1 }}>
                <FHSelect
                  data-testid="groupLocationField"
                  sx={{
                    flexGrow: 1,
                    maxWidth: '100%',
                    '&.Mui-disabled': {
                      WebkitTextFillColor: FORTA_GRAY3,
                      cursor: 'not-allowed',
                    },
                    boxShadow: 'none',
                  }}
                  inputHeight={40}
                  disabled={!isEditable || !selectedClient || groupSNLoading}
                  displayEmpty
                  renderValue={
                    !location
                      ? () => <Typography color={FORTA_GRAY3}>Select location</Typography>
                      : undefined
                  }
                  error={
                    validate &&
                    selectedClient &&
                    families?.length < GROUP_SN_MIN_FAMILIES &&
                    !location
                  }
                  options={SUPERVISION_LOCATION_V2}
                  optionDataTestIdPrefix="groupLocationField"
                  value={location}
                  selectFirstOption={false}
                  onUpdate={(value: SelectValueType) => {
                    setLocation(value);
                  }}
                />
              </Box>
            </Grid>
            <Grid item md={1.5} sm={12} xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <FHButton
                  data-testid="addFamilyButton"
                  startIcon={
                    <PlusIcon color={disableAddFamily ? FORTA_BLUE.light3 : FORTA_BLACK} />
                  }
                  disabled={disableAddFamily || groupSNLoading}
                  sx={{
                    backgroundColor: FORTA_WHITE,
                    border: `1px solid ${FORTA_BLUE.light2}`,
                    color: FORTA_GRAY4,
                    mt: 4,
                    width: '125px',
                    '&:hover': {
                      backgroundColor: FORTA_WHITE,
                      border: `1px solid ${FORTA_BLUE.light2}`,
                      color: FORTA_GRAY4,
                    },
                    '&.Mui-disabled': {
                      backgroundColor: FORTA_WHITE,
                      border: `1px solid ${FORTA_BLUE.light2}`,
                      color: FORTA_BLUE.light2,
                    },
                  }}
                  onClick={handleAddNewFamily}
                >
                  Family
                </FHButton>
              </Box>
            </Grid>
          </Grid>
        </Box>
      )}
      <Box>
        {families?.length > 0 && !isMobile && <FamilyListHeader />}
        {families?.map((family) => (
          <FamilyRowItem
            key={family.id}
            id={family.id}
            gpsnId={groupParentSN?.id}
            gpsnStartTime={moment(groupParentSN?.date_start)}
            gpsnEndTime={moment(groupParentSN?.date_start).add(groupParentSN?.duration, 'seconds')}
            location={family.location}
            duration={family.duration}
            gpsnDuration={groupParentSN?.duration ?? 0}
            disabled={groupSNLoading}
            attendance={family}
            isEditable={isEditable}
            providerAssignments={providerAssignments ?? []}
            providerAssignmentLoading={providerAssignmentLoading}
            onDelete={handleFamilyAttendanceDelete}
          />
        ))}
      </Box>
    </FBoxContainer>
  );
}

export default Families;
